import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ticket, Loader2, Plus, Trash2, Gift } from "lucide-react";
import { toast } from "sonner";

export default function Roue() {
  const { user } = useAuth();
  const isAdmin = user && ["owner", "admin"].includes(user.role);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [prizes, setPrizes] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/wheel"); setEnabled(!!data.enabled); setPrizes(data.prizes || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addPrize = () => setPrizes((p) => [...p, { label: "", weight: 1 }]);
  const updPrize = (i, k, v) => setPrizes((p) => p.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const delPrize = (i) => setPrizes((p) => p.filter((_, j) => j !== i));
  const save = async () => {
    try {
      const clean = prizes.filter((p) => (p.label || "").trim()).map((p) => ({ label: p.label.trim(), weight: Number(p.weight || 1) }));
      await api.put("/wheel", { enabled, prizes: clean }); toast.success("Roue enregistrée"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const spin = async () => {
    setSpinning(true); setResult(null);
    try { const { data } = await api.post("/wheel/spin"); setTimeout(() => { setResult(data.prize); setSpinning(false); }, 900); }
    catch (e) { setSpinning(false); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Roue de la fortune" subtitle="Un jeu promotionnel pour fidéliser vos clients" icon={Ticket} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> : (
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center text-center" data-testid="wheel-play">
          <div className={`w-40 h-40 rounded-full border-8 border-emerald-500/30 flex items-center justify-center mb-5 transition-transform ${spinning ? "animate-spin" : ""}`}>
            <Gift className="w-14 h-14 text-emerald-500" />
          </div>
          {result && <div data-testid="wheel-result" className="mb-4 text-lg font-heading font-bold text-emerald-600">🎉 {result}</div>}
          <Button data-testid="wheel-spin" onClick={spin} disabled={spinning || !enabled || prizes.length === 0} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">
            {spinning ? "La roue tourne…" : "Tourner la roue"}
          </Button>
          {(!enabled || prizes.length === 0) && <p className="text-xs text-slate-400 mt-3">Configurez et activez la roue pour jouer.</p>}
        </div>

        {isAdmin && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3" data-testid="wheel-config">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-800"><input type="checkbox" data-testid="wheel-enabled" checked={enabled} onChange={(e)=>setEnabled(e.target.checked)} />Activer la roue</label>
            <div className="space-y-2">
              {prizes.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input data-testid={`prize-label-${i}`} placeholder="Lot (ex. -10%)" value={p.label} onChange={(e)=>updPrize(i,"label",e.target.value)} className="bg-slate-50 border-slate-200" />
                  <Input data-testid={`prize-weight-${i}`} type="number" title="Probabilité relative" value={p.weight} onChange={(e)=>updPrize(i,"weight",e.target.value)} className="bg-slate-50 border-slate-200 w-24" />
                  <Button size="icon" variant="ghost" onClick={()=>delPrize(i)} className="text-red-500 h-8 w-8 shrink-0"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
            <Button data-testid="prize-add" variant="outline" onClick={addPrize} className="border-slate-200"><Plus className="w-4 h-4 mr-1" />Ajouter un lot</Button>
            <div><Button data-testid="wheel-save" onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold mt-2">Enregistrer la roue</Button></div>
            <p className="text-[11px] text-slate-400">Le poids détermine la probabilité relative de chaque lot. Le tirage est réellement aléatoire côté serveur.</p>
          </div>
        )}
      </div>
      )}
    </div>
  );
}
